import axios from "axios";
import { PaginatedResponse } from "../types/api";
import { API_BASE_URL_NO_API } from "../utils/apiConfig";

const API_BASE_URL = API_BASE_URL_NO_API;

export interface TrackingEvent {
    id: number;
    status: string;
    status_display: string;
    description: string;
    location: string;
    timestamp: string;
    source: string;
    source_display: string;
    created_by: number | null;
    created_by_name: string | null;
    created_at: string;
}

export interface ShipmentApiResponse {
    id: number;
    tracking_number: string;
    sender: string;
    receiver: string;
    shipment_date: string;
    delivery_date: string | null;
    status: string;
    status_display: string;
    weight: string;
    carrier: string;
    carrier_display: string;
    carrier_tracking_number: string;
    document: number | null;
    notes: string;
    created_at: string;
    updated_at: string;
    created_by: number | null;
    created_by_name: string | null;
    allowed_transitions: string[];
    tracking_events: TrackingEvent[];
}

export interface ShipmentListItem {
    id: number;
    tracking_number: string;
    sender: string;
    receiver: string;
    shipment_date: string;
    delivery_date: string | null;
    status: string;
    status_display: string;
    weight: string;
    carrier: string;
    carrier_display: string;
    carrier_tracking_number: string;
    notes: string;
    created_at: string;
    created_by_name: string | null;
    tracking_events_count: number;
}

export type PaginatedShipmentsResponse = PaginatedResponse<ShipmentListItem>;

export interface GetShipmentsParams {
    search?: string;
    status?: string;
    carrier?: string;
    page?: number;
    page_size?: number;
}

export interface CreateShipmentRequest {
    sender: string;
    receiver: string;
    shipment_date?: string;
    status?: string;
    weight: number;
    carrier?: string;
    carrier_tracking_number?: string;
    document_id?: number | null;
    notes?: string;
}

export interface UpdateShipmentRequest {
    sender?: string;
    receiver?: string;
    shipment_date?: string;
    weight?: number;
    carrier?: string;
    carrier_tracking_number?: string;
    document_id?: number | null;
    notes?: string;
}

export interface CreateTrackingEventRequest {
    status?: string;
    description: string;
    location?: string;
    timestamp?: string;
}

export interface StatusOption {
    value: string;
    label: string;
}

const getBaseUrl = () => {
    return API_BASE_URL.endsWith('/api') ? API_BASE_URL.slice(0, -4) : API_BASE_URL;
};

export const shipmentsApi = {
    getShipments: async (params?: GetShipmentsParams): Promise<PaginatedShipmentsResponse> => {
        const queryParams = new URLSearchParams();
        if (params?.search) queryParams.append("search", params.search);
        if (params?.status) queryParams.append("status", params.status);
        if (params?.carrier) queryParams.append("carrier", params.carrier);
        if (params?.page) queryParams.append("page", params.page.toString());
        if (params?.page_size) queryParams.append("page_size", params.page_size.toString());

        const queryString = queryParams.toString();
        const baseUrl = getBaseUrl();
        const url = `${baseUrl}/api/shipments/${queryString ? `?${queryString}` : ""}`;
        const response = await axios.get<PaginatedShipmentsResponse>(url);
        return response.data;
    },

    getShipment: async (id: number): Promise<ShipmentApiResponse> => {
        const baseUrl = getBaseUrl();
        const url = `${baseUrl}/api/shipments/${id}/`;
        const response = await axios.get<ShipmentApiResponse>(url);
        return response.data;
    },

    createShipment: async (data: CreateShipmentRequest): Promise<ShipmentApiResponse> => {
        const baseUrl = getBaseUrl();
        const url = `${baseUrl}/api/shipments/`;
        const response = await axios.post<ShipmentApiResponse>(url, data);
        return response.data;
    },

    updateShipment: async (id: number, data: UpdateShipmentRequest): Promise<ShipmentApiResponse> => {
        const baseUrl = getBaseUrl();
        const url = `${baseUrl}/api/shipments/${id}/`;
        const response = await axios.patch<ShipmentApiResponse>(url, data);
        return response.data;
    },

    deleteShipment: async (id: number): Promise<void> => {
        const baseUrl = getBaseUrl();
        const url = `${baseUrl}/api/shipments/${id}/`;
        await axios.delete(url);
    },

    changeStatus: async (id: number, status: string): Promise<ShipmentApiResponse> => {
        const baseUrl = getBaseUrl();
        const url = `${baseUrl}/api/shipments/${id}/change_status/`;
        const response = await axios.post<ShipmentApiResponse>(url, { status });
        return response.data;
    },

    getTrackingEvents: async (id: number): Promise<TrackingEvent[]> => {
        const baseUrl = getBaseUrl();
        const url = `${baseUrl}/api/shipments/${id}/tracking/`;
        const response = await axios.get<TrackingEvent[]>(url);
        return response.data;
    },

    addTrackingEvent: async (id: number, data: CreateTrackingEventRequest): Promise<TrackingEvent> => {
        const baseUrl = getBaseUrl();
        const url = `${baseUrl}/api/shipments/${id}/tracking/`;
        const response = await axios.post<TrackingEvent>(url, data);
        return response.data;
    },

    getStatuses: async (): Promise<StatusOption[]> => {
        const baseUrl = getBaseUrl();
        const url = `${baseUrl}/api/shipments/statuses/`;
        const response = await axios.get<StatusOption[]>(url);
        return response.data;
    },

    getCarriers: async (): Promise<StatusOption[]> => {
        const baseUrl = getBaseUrl();
        const url = `${baseUrl}/api/shipments/carriers/`;
        const response = await axios.get<StatusOption[]>(url);
        return response.data;
    },
};
